import React from 'react'
import { Box, Link as MuiLink } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'
import { useEntityCodeModal } from './EntityCodeModalContext'
import { entityCodeHref, type EntityCodeType } from './entityCodeTypes'

export const entityCodeLinkSx = {
  color: banhangKiotLike.linkBlue,
  textDecoration: 'none',
  fontWeight: 'inherit',
  lineHeight: 1.35,
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
} as const

export type EntityCodeLinkProps = {
  type: EntityCodeType
  id: string | number | null | undefined
  /** Nội dung hiển thị — thường là mã; mặc định = `code`. */
  code?: string | null
  children?: React.ReactNode
  onChanged?: () => void
  sx?: SxProps<Theme>
}

/**
 * Mã liên kết: click → modal chi tiết (có nút Chỉnh sửa). Ctrl/⌘/chuột giữa → tab mới như link thường.
 * Không có `id` → chỉ hiện text.
 */
export const EntityCodeLink: React.FC<EntityCodeLinkProps> = ({ type, id, code, children, onChanged, sx }) => {
  const modal = useEntityCodeModal()
  const idStr = id == null ? '' : String(id).trim()
  const label = children ?? code ?? '—'
  if (!idStr) return <>{label}</>
  return (
    <MuiLink
      href={entityCodeHref(type, idStr)}
      underline="none"
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation()
        if (!modal || e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return
        e.preventDefault()
        modal.openEntityDetail({ type, id: idStr, code: code ?? undefined, onChanged })
      }}
      sx={[entityCodeLinkSx, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])]}
    >
      {label}
    </MuiLink>
  )
}

export type EntityCodeLinkItem = { id?: string | number | null; code?: string | null }

/** Nhiều mã → mỗi mã 1 dòng (`inline`: cùng 1 dòng, cách nhau dấu phẩy). Rỗng → «—». */
export const EntityCodeLinkList: React.FC<{
  type: EntityCodeType
  items: EntityCodeLinkItem[]
  onChanged?: () => void
  inline?: boolean
}> = ({ type, items, onChanged, inline = false }) => {
  const links = items.filter((p) => p.id != null && String(p.id).trim() && p.code)
  if (links.length === 0) return <>—</>
  if (inline) {
    return (
      <Box component="span" title={links.map((p) => p.code).join(', ')} sx={{ whiteSpace: 'nowrap' }}>
        {links.map((p, i) => (
          <React.Fragment key={String(p.id)}>
            {i > 0 ? ', ' : null}
            <EntityCodeLink type={type} id={p.id} code={p.code} onChanged={onChanged} />
          </React.Fragment>
        ))}
      </Box>
    )
  }
  return (
    <Box
      component="span"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, py: 0.25 }}
    >
      {links.map((p) => (
        <EntityCodeLink key={String(p.id)} type={type} id={p.id} code={p.code} onChanged={onChanged} />
      ))}
    </Box>
  )
}

export default EntityCodeLink
